import { dataState, controlState, communicationState } from '../components/helpers/reducerHelpers'
import { createAction, handleActions } from 'redux-actions'
import dataManager from '../businesslayer/minutesDataManager'

import { transientStorageManager } from '../businesslayer/minutesSessionStore'

import validator from '../businesslayer/validation/takerActionValidator'
import { saveAction } from 'businesslayer/api/actions'
import { getContacts } from 'businesslayer/api/contacts'

export const actions = {
    createSectionAction: createAction('CREATE_SECTION_ACTION'),
    editSectionAction: createAction('EDIT_SECTION_ACTION'),
    validateSectionAction: createAction('VALIDATE_SECTION_ACTION'),

    loadContacts: createAction('LOAD_CONTACTS', (payload = false) => {
        return getContacts(payload, actions.loadContacts.bind(this, payload))
    }),

    loadContactsPending: createAction('LOAD_CONTACTS_PENDING'),
    loadContactsFulfilled: createAction('LOAD_CONTACTS_FULFILLED'),
    loadContactsRejected: createAction('LOAD_CONTACTS_REJECTED'),

    prepareSaveAction: createAction('PREPARE_SAVE_ACTION'),

    saveNewAction: createAction('SAVE_NEW_ACTION', (payload) => {
        const { isAdmin, ...actionData } = payload
        return saveAction(actionData, actions.saveNewAction.bind(this, payload), isAdmin)
    }),

    saveNewActionPending: createAction('SAVE_NEW_ACTION_PENDING'),
    saveNewActionFulfilled: createAction('SAVE_NEW_ACTION_FULFILLED'),
    saveNewActionRejected: createAction('SAVE_NEW_ACTION_REJECTED'),

    saveExistingAction: createAction('SAVE_EXISTING_ACTION', (payload) => {
        const { isAdmin, ...actionData } = payload
        return saveAction(actionData, actions.saveExistingAction.bind(this, payload), isAdmin)
    }),

    saveExistingActionPending: createAction('SAVE_EXISTING_ACTION_PENDING'),
    saveExistingActionFulfilled: createAction('SAVE_EXISTING_ACTION_FULFILLED'),
    saveExistingActionRejected: createAction('SAVE_EXISTING_ACTION_REJECTED'),

    updateReminderSettings: createAction('UPDATE_REMINDER_SETTINGS'),
    clearAllReminderSettings: createAction('CLEAR_ALL_REMINDER_SETTINGS'),
    clearSavedAction: createAction('CLEAR_SAVED_ACTION'),
    resetActionEditorSession: createAction('RESET_ACTION_EDITOR_SESSION')
}

const initialState = {
    controlState: {
        readyToSave: false
    },
    dataState: {
        currentAction: null,
        savedAction: null,
        section: null,
        minutesId: null,
        allContacts: null,
        dateFormat: null,
        dateInputMask: null,
        reminderSettings: {} // Store reminder settings by action ID
    },
    communicationState: {
        saveComplete: false,
        isSavingExisting: false
    },
    sessionState: {}
}

let updatedState = null
const reducer = handleActions(
    {
        [actions.createSectionAction]: (state, action) => {
            const minutesId = action.payload

            updatedState = dataState(state, {
                dateInputMask: transientStorageManager.dateFormatInput,
                minutesId: minutesId,
                currentAction: dataManager.createDefaultMinuteAction(minutesId)
            })

            return controlState(updatedState, {
                saveComplete: false,
                readyToSave: false,
                savedAction: null
            })
        },

        [actions.editSectionAction]: (state, action) => {
            const sectionAction = action.payload
            updatedState = dataState(state, {
                dateInputMask: transientStorageManager.dateFormatInput,
                minutesId: sectionAction.minutesId,
                currentAction: sectionAction
            })

            return controlState(updatedState, {
                saveComplete: false,
                readyToSave: false,
                savedAction: null
            })
        },

        [actions.validateSectionAction]: (state, action) => {
            const preValidatedAction = action.payload

            const currentAction = validator.validateAction(preValidatedAction)

            return dataState(state, {
                currentAction: Object.assign({}, currentAction)
            })
        },

        [actions.prepareSaveAction]: (state, action) => {
            let currentAction = action.payload
            currentAction = validator.validateAction(currentAction)
            const noErrors =
                !currentAction.validationErrors || currentAction.validationErrors.size === 0

            updatedState = dataState(state, {
                currentAction: Object.assign({}, currentAction)
            })

            if (noErrors) {
                return controlState(updatedState, { readyToSave: true })
            }
            return controlState(updatedState, { readyToSave: false })
        },

        [actions.loadContactsFulfilled]: (state, action) => {
            const allContacts = action.payload.contacts ? action.payload.contacts : null

            //This function is called quite often so we use this function to clean up savedAction
            //so newly opened minutes would not bull this saved action for previously opened minutes
            return dataState(state, {
                allContacts: allContacts,
                dateFormat: transientStorageManager.dateFormat,
                savedAction: null
            })
        },

        [actions.saveNewActionPending]: (state, action) => {
            updatedState = dataState(state, { savedAction: null })
            return communicationState(updatedState, {
                saveComplete: false,
                isSavingExisting: true
            })
        },

        [actions.saveNewActionFulfilled]: (state, action) => {
            updatedState = state
            const saveResult = action.payload.actionItems

            const key = Object.keys(saveResult)[0]

            if (key) {
                const savedActionResult = saveResult[key]
                const attrs = savedActionResult.attributes || {}

                let assignees = attrs.assignees

                if (assignees) {
                    assignees = assignees.map((c) => {
                        return {
                            email: c.email,
                            id: c.id,
                            text: c.display_name,
                            self_manage: c.self_manage
                        }
                    })
                }

                const sectionId =
                    attrs.minutesSectionId ??
                    attrs.minutes_section_id ??
                    attrs.minutesSectionID ??
                    attrs.minutes_sectionId

                const savedAction = {
                    id: savedActionResult.id,
                    sortOrder: attrs.sortOrder,
                    dueDate: attrs.dueDate,
                    minutesId: attrs.minutesDocumentId,
                    minutesSectionId: sectionId,
                    minutes_section_id: sectionId,
                    text: (attrs.text ?? '')
                        .replace(/^(["]*)/g, '')
                        .replace(/(["]*)$/g, ''),
                    completionStatus: attrs.completionStatus,
                    notificationStatus: attrs.notificationStatus,
                    assignees: assignees,
                    createdAt: attrs.createdAt,
                    updatedAt: attrs.updatedAt,
                    isNew: true,
                    notes: attrs.note,
                    status: attrs.status,
                    reminders: attrs.reminders || [],
                    section_name: attrs.section_name,
                    remember_reminder_settings:
                        (attrs.rememberReminderSettings ?? attrs.remember_reminder_settings) === true
                }

                updatedState = dataState(updatedState, { savedAction: savedAction })
            }

            return communicationState(updatedState, {
                saveComplete: true,
                readyToSave: false,
                isSavingExisting: false
            })
        },

        [actions.saveNewActionRejected]: (state, action) => {
            updatedState = dataState(state, { savedAction: null })
            return communicationState(updatedState, {
                saveComplete: true,
                readyToSave: false,
                isSavingExisting: false
            })
        },

        [actions.saveExistingActionPending]: (state, action) => {
            updatedState = dataState(state, { savedAction: null })
            return communicationState(updatedState, { saveComplete: false, isSavingExisting: true })
        },

        [actions.saveExistingActionFulfilled]: (state, action) => {
            updatedState = state
            const saveResult = action.payload.actionItems

            const key = Object.keys(saveResult)[0]

            if (key) {
                const savedActionResult = saveResult[key]
                const attrs = savedActionResult.attributes || {}

                let assignees = attrs.assignees

                if (assignees) {
                    assignees = assignees.map((c) => {
                        return {
                            email: c.email,
                            id: c.id,
                            text: c.display_name,
                            self_manage: c.self_manage
                        }
                    })
                }

                const sectionId =
                    attrs.minutesSectionId ??
                    attrs.minutes_section_id ??
                    attrs.minutesSectionID ??
                    attrs.minutes_sectionId

                //Note: isUpdated flag
                const savedAction = {
                    id: savedActionResult.id,
                    sortOrder: attrs.sortOrder,
                    dueDate: attrs.dueDate,
                    minutesId: attrs.minutesDocumentId,
                    minutesSectionId: sectionId,
                    minutes_section_id: sectionId,
                    text: (attrs.text ?? '')
                        .replace(/^(["]*)/g, '')
                        .replace(/(["]*)$/g, ''),
                    completionStatus: attrs.completionStatus,
                    notificationStatus: attrs.notificationStatus,
                    assignees: assignees,
                    isUpdated: true,
                    createdAt: attrs.createdAt,
                    updatedAt: attrs.updatedAt,
                    notes: attrs.note,
                    status: attrs.status,
                    reminders: attrs.reminders || [],
                    remember_reminder_settings:
                        (attrs.rememberReminderSettings ?? attrs.remember_reminder_settings) === true,
                    section_name: attrs.section_name
                }

                updatedState = dataState(updatedState, { savedAction: savedAction })
            }

            return communicationState(updatedState, {
                saveComplete: true,
                readyToSave: false,
                isSavingExisting: false
            })
        },

        [actions.saveExistingActionRejected]: (state, action) => {
            updatedState = dataState(state, { savedAction: null })
            return communicationState(updatedState, {
                saveComplete: true,
                readyToSave: false,
                isSavingExisting: false
            })
        },

        [actions.updateReminderSettings]: (state, action) => {
            const { actionId, reminderSettings } = action.payload
            const currentReminderSettings = state.dataState.reminderSettings || {}
            
            return dataState(state, {
                reminderSettings: {
                    ...currentReminderSettings,
                    [actionId]: reminderSettings
                }
            })
        },

        [actions.clearAllReminderSettings]: (state, action) => {
            return dataState(state, {
                reminderSettings: {}
            })
        },

        [actions.clearSavedAction]: (state) => {
            if (!state.dataState?.savedAction) return state;
            
            return dataState(state, {
                savedAction: null
            });
        },

        [actions.resetActionEditorSession]: (state) => {
            updatedState = dataState(state, {
                currentAction: null,
                savedAction: null,
                section: null,
                minutesId: null
            })
            updatedState = controlState(updatedState, {
                readyToSave: false
            })
            return communicationState(updatedState, {
                saveComplete: false,
                isSavingExisting: false
            })
        }
    },
    initialState
)

export default reducer